import { FC, useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { ETableStructureType } from 'ui/EasTable/types';
import { getCountryListData } from 'redux/reducers/country/selectors';
import {
	createPpwrEmCountryExclusionRequest,
	resetSavePpwrEmCountryExclusionSuccess,
	updatePpwrEmCountryExclusionRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmCountryExclusionSaveLoading,
	getPpwrEmCountryExclusionSaveSuccess,
} from 'redux/reducers/ppwr/selectors';
import {
	TCreateOrUpdatePpwrEmCountryExclusionRequest,
	TPpwrEmCountryExclusion,
} from 'redux/reducers/ppwr/types';

type TCountryExclusionDialogProps = {
	emId: number;
	/** Exclusion being edited, `null` when a new one is added. */
	exclusion: TPpwrEmCountryExclusion | null;
	/** Countries that already have an exclusion and cannot be picked again. */
	excludedCountryIds: number[];
	onClose: () => void;
};

type TFormValues = {
	countryId: number | null;
	reason: string;
	reportsDoneBy: string;
	lucid: string;
	/** The date input works with `YYYY-MM-DD`. */
	validTill: string;
};

const validationSchema = Yup.object().shape({
	countryId: Yup.number().nullable().required('Select a country'),
});

const emptyToNull = (value: string) => (value.trim() === '' ? null : value.trim());

/** Mounted only while open, so the form starts from the passed exclusion. */
export const CountryExclusionDialog: FC<TCountryExclusionDialogProps> = ({
	emId,
	exclusion,
	excludedCountryIds,
	onClose,
}) => {
	const dispatch = useDispatch();
	const countries = useSelector(getCountryListData);
	const saveLoading = useSelector(getPpwrEmCountryExclusionSaveLoading);
	const saveSuccess = useSelector(getPpwrEmCountryExclusionSaveSuccess);

	/** EU countries without an exclusion yet, plus the country of the edited one. */
	const countryList = useMemo(
		() =>
			(countries ?? [])
				.filter(
					(country) =>
						country.id === exclusion?.countryId ||
						(country.isEu && !excludedCountryIds.includes(country.id)),
				)
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((country) => ({ code: country.isoAlpha2, label: country.name, number: country.id })),
		[countries, exclusion, excludedCountryIds],
	);

	const formik = useFormik<TFormValues>({
		initialValues: {
			countryId: exclusion?.countryId ?? null,
			reason: exclusion?.reason ?? '',
			reportsDoneBy: exclusion?.reportsDoneBy ?? '',
			lucid: exclusion?.lucid ?? '',
			validTill: exclusion?.validTill?.slice(0, 10) ?? '',
		},
		validationSchema,
		onSubmit: (values) => {
			const body: TCreateOrUpdatePpwrEmCountryExclusionRequest = {
				emId,
				countryId: values.countryId,
				reason: emptyToNull(values.reason),
				reportsDoneBy: emptyToNull(values.reportsDoneBy),
				lucid: emptyToNull(values.lucid),
				validTill: emptyToNull(values.validTill),
			};
			dispatch(
				exclusion
					? updatePpwrEmCountryExclusionRequest({ id: exclusion.id, body })
					: createPpwrEmCountryExclusionRequest(body),
			);
		},
	});

	useEffect(() => {
		if (!saveSuccess) return;
		dispatch(resetSavePpwrEmCountryExclusionSuccess());
		onClose();
	}, [dispatch, saveSuccess, onClose]);

	const countryError =
		formik.touched.countryId && formik.errors.countryId ? formik.errors.countryId : undefined;

	return (
		<Dialog open maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
			<DialogTitle color="primary">
				{exclusion ? 'Edit Country Exclusion' : 'Add Country Exclusion'}
			</DialogTitle>
			<form onSubmit={formik.handleSubmit} noValidate>
				<DialogContent dividers>
					<CountrySelectMenu
						type={ETableStructureType.COUNTRY_NUMBER}
						countryList={countryList}
						countryNumber={formik.values.countryId}
						onChangeNumber={(countryId) => formik.setFieldValue('countryId', countryId)}
						onBlur={() => formik.setFieldTouched('countryId', true)}
						error={!!countryError}
						helperText={countryError}
					/>
					<TextField
						fullWidth
						multiline
						minRows={3}
						size="small"
						label="Reason"
						name="reason"
						value={formik.values.reason}
						onChange={formik.handleChange}
						sx={{ mt: 1 }}
					/>
					<TextField
						fullWidth
						size="small"
						label="Reports Done By"
						name="reportsDoneBy"
						value={formik.values.reportsDoneBy}
						onChange={formik.handleChange}
						sx={{ mt: 2 }}
					/>
					<TextField
						fullWidth
						size="small"
						label="LUCID"
						name="lucid"
						value={formik.values.lucid}
						onChange={formik.handleChange}
						sx={{ mt: 2 }}
					/>
					<TextField
						fullWidth
						size="small"
						type="date"
						label="Valid Till"
						name="validTill"
						value={formik.values.validTill}
						onChange={formik.handleChange}
						InputLabelProps={{ shrink: true }}
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={onClose} color="inherit" disabled={saveLoading}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={saveLoading || !formik.dirty}>
						{saveLoading ? <ButtonCircularProgress /> : exclusion ? 'Save' : 'Add'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
